<script setup lang="ts">
import { AArrowUp, Bean, BookOpenText, ChevronDown, Eraser, FlaskConical, Info, InfoIcon, Layers, LoaderCircle, Lock, MessageSquareMore, MessageSquareText, Paintbrush, Palette, Pickaxe, PlaneTakeoff, Plus, Settings2, Shirt, Smile, Ticket, WandSparkles } from 'lucide-vue-next'
import { computed, defineAsyncComponent, ref, shallowRef, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { addYears } from 'date-fns'
import { useConfirm } from 'primevue/useconfirm'
import ConfirmPopup from 'primevue/confirmpopup'
import PromptInput from '@/components/generator/common/PromptInput.vue'
import Tools from '@/utils/tools'
import { useDynamicModalStore } from '@/stores/dynamicModalStore'
import { DynamicModalType, ENotificationType, RenderType } from '@/types'
import { useGeneratorStore } from '@/stores/GeneratorStore'
import { CTweakActions } from '@/constants'
import { useAuthStore } from '@/stores/authStore'
import GeneratorTemplate from '@/components/generator/GeneratorTemplate.vue'
import GeneratorPanel from '@/components/generator/common/GeneratorPanel.vue'
import ModifierSlot from '@/components/generator/generator/modifiers/ModifierSlot.vue'
import { useGeneratorClientStore } from '@/stores/GeneratorClient'
import { useGraphicStyles } from '@/composables/useGraphicStyles'
import { useNotificationStore } from '@/stores/notificationStore'
import ProductTour from '@/components/generator/generator/ProductTour.vue'
import EnergyIcon from '@/icons/EnergyIcon.vue'

const confirm = useConfirm()
const { t } = useI18n()
const MultiChoice = defineAsyncComponent(() => import('@/components/generator/common/GeneratorMultipleChoice.vue'))
const GeneratorSwitch = defineAsyncComponent(() => import('@/components/generator/common/GeneratorSwitch.vue'))
const GeneratorSeed = defineAsyncComponent(() => import('@/components/generator/common/GeneratorSeed.vue'))
const InpaintCanvas = defineAsyncComponent(() => import('@/components/generator/tweak/Inpaint.vue'))
const genStore = useGeneratorStore()
const authStore = useAuthStore()
const genClientStore = useGeneratorClientStore()

const {
	tweakImages,
	tweakPrompt,
	convertStyle,
	disableInpaint,
	textLimitPos,
	textLimitNeg,
	tweakSettingCost,
	tweakCost,
	nextReset,
	computedTweakImage,
	newModifiers,
	newModifiersCount,
} = storeToRefs(genStore)

const {
	tweakProgress,
} = storeToRefs(genClientStore)

const { user, isPremium, hasDailyTickets } = storeToRefs(authStore)
const discordLink = ref<string>(import.meta.env.VITE_DISCORD_INVITE_LINK ?? '')

const timer = Tools.createTimer(0, 1010)

const blink = shallowRef<boolean>(false)
const inpaint_canvas = ref()
const enoughTickets = computed(() => {
	return user.value.generation_token >= tweakCost.value || user.value.daily_token >= tweakCost.value
})
const selectedInpaint = ref<string>('inpaint')
const inpaintOptions = ref([
	{
		name: 'Style to Style',
		value: 'style_convert',
	},
	{
		name: 'Inpaint',
		value: 'inpaint',
	},
/* 	{
		name: 'Face Restore',
		value: 'face_restore',
	}, */
])

async function generate() {
	if (tweakImages.value.length) {
		if (selectedInpaint.value === 'inpaint') {
			const mask = inpaint_canvas.value.exportMaskAsImage()
			if (!mask) {
				useNotificationStore().push(ENotificationType.AppError, {
					key: `notification.empty_tweak_mask`,
					delay: 4000,
				})
				return
			}
			tweakPrompt.value.mask = mask
			await genStore.doInpaint()
		}
		else {
			await genStore.styleConvert()
		}
	}
}

function clearPrompts() {
	tweakPrompt.value.prompt = ''
	tweakPrompt.value.neg_prompt = ''
}

const largerFont = shallowRef<boolean>(Tools.getCookie('larger-font') === 'true')

watch(() => largerFont.value, () => {
	Tools.setCookie('larger-font', largerFont.value.toString(), addYears(new Date(), 1), `.${import.meta.env.VITE_DOMAIN}`, '/')
})

function toggleInfoReset(event: any) {
	confirm.require({
		target: event.currentTarget,
		group: 'info-reset',
	})
}

const inpaintMode = shallowRef<boolean>(false)

function changeInpaintMode() {
	if ((computedTweakImage.value?.is_user_gen || !tweakImages.value.length) && isPremium.value) {
		selectedInpaint.value = selectedInpaint.value === 'style_convert' ? 'inpaint' : 'style_convert'
		inpaintMode.value = !inpaintMode.value
	}
}
</script>

<template>
	<ProductTour
		v-if="authStore.isAuth()"
		mode="tweak"
	/>
	<GeneratorTemplate>
		<template #leftContainer />
		<template #rightContainer>
			<div class="flex flex-col gap-6 w-full">
				<div class="flex-center gap-2 -mb-2">
					<router-link
						to="/guide#tweak-panel"
						class="button-light-purple no-underline w-fit"
					>
						{{ t('common.guide') }}
						<BookOpenText class="lucide-button-icon" />
					</router-link>
					<a
						target="_blank"
						:href="discordLink"
						class="button-light-blue no-underline w-fit"
					>
						Discord
						<i class="pi pi-discord" />
					</a>
				</div>
				<div class="setting-container flex-wrap !items-end">
					<div class="!w-full flex flex-col gap-2 items-center ">
						<router-link
							v-if="!isPremium"
							to="/tickets"
							class="no-underline select-none duration-300 hover:bg-slate-700 cursor-pointer hover:text-white text-xsp w-fit text-slate-300 flex-center gap-1 border-[1px] border-slate-600 border-solid py-1 px-2 rounded-full"
						>
							<InfoIcon class="lucide-button-icon-xs text-slate-300" />
							{{ t('generator.upgrade_style_conversion') }}
						</router-link>
						<div
							class="w-[320px] h-[40px] relative cursor-pointer flex flex-nowrap bg-slate-800 rounded-full  gap-4 p-1 mx-auto justify-between items-center"
							@click="changeInpaintMode"
						>
							<div
								:class="[
									inpaintMode ? 'translate-x-[162px]' : 'translate-x-0',
								]"
								class="absolute w-[150px] left-1 rounded-full bg-slate-500 h-[34px] z-[1] transition-transform duration-300"
							/>
							<div
								class="z-[2] flex-center w-6/12 rounded-full gap-1 text-xsp"
							>
								<Paintbrush class="lucide-button-icon-sm" /> {{ t('generator.inpaint') }}
							</div>
							<div
								:class="{
									'opacity-50': (!computedTweakImage?.is_user_gen && tweakImages.length) || !isPremium,
								}"
								class="z-[2] flex-center w-6/12 rounded-full gap-1 text-xsp"
							>
								<component
									:is="!isPremium ? Lock : Palette"
									class="lucide-button-icon-sm"
								/>
								<template v-if="computedTweakImage?.is_user_gen || !tweakImages.length">
									{{ t('generator.style_convert') }}
								</template>
								<template v-else>
									{{ t('generator.disabled_legacy_image') }}
								</template>
							</div>
						</div>
					</div>

					<div
						v-if="selectedInpaint === 'style_convert'"
						class="bg-slate-800/70 border-[1px] h-full w-full border-solid border-slate-700/60 rounded-xl p-4 flex items-center justify-between cursor-pointer hover:bg-slate-700/70 transition-colors duration-200"
						@click="useDynamicModalStore().showModal(DynamicModalType.SelectGenStyle, {
							props: {
								mode: 'tweak',
							},
						})"
					>
						<div class="flex-center gap-3">
							<img
								width="40"
								class="rounded-lg object-cover border-[1px] border-solid border-slate-600/50"
								:src="genStore.getStylePreview(useGraphicStyles().getStyleByName(convertStyle) as any).webp"
							>
							<div class="flex flex-col items-start">
								<span class="text-slate-200 font-medium sm:text-base text-sm">{{ t('generator.convert_to_style') }}</span>
								<span class="text-violet-300 sm:text-sm text-xsp">{{ convertStyle }}</span>
							</div>
						</div>
						<ChevronDown class="lucide-button-icon-xs text-slate-400 ml-2" />
					</div>
				</div>
				<div
					v-if="selectedInpaint !== 'style_convert'"
					class="prompt-container-compact"
				>
					<PromptInput
						id="positive-prompt"
						v-model="tweakPrompt.prompt"
						:title="t('generator.prompt')"
						:icon="MessageSquareText"
						placeholder="smiling, nose blush"
						:text-limit="textLimitPos"
						:limit-message="t('generator.lengthy_prompt')"
						:use-larger-font="largerFont"
						:is-blinking="blink"
						info="Describe the changes you wish to make to the painted area."
						type="positive"
						mode="tweak"
					>
						<!-- Header actions slot - version compacte -->
						<template #header-actions>
							<div class="prompt-actions-header">
								<div class="action-group">
									<Button
										v-tooltip.bottom="{ value: t('tooltips.larger_font'), showDelay: 500 }"
										class="action-btn-mini"
										:class="{ active: largerFont }"
										@click="largerFont = !largerFont"
									>
										<AArrowUp class="lucide-button-icon-xss" />
									</Button>
									<Button
										v-tooltip.bottom="{ value: t('tooltips.clear_prompts'), showDelay: 500 }"
										class="action-btn-mini"
										@click="clearPrompts"
									>
										<Eraser class="lucide-button-icon-xss" />
									</Button>
								</div>
							</div>
						</template>
					</PromptInput>

					<PromptInput
						id="negative-prompt"
						v-model="tweakPrompt.neg_prompt"
						:title="t('generator.neg_prompt')"
						:icon="MessageSquareMore"
						placeholder="freckles, glasses"
						:text-limit="textLimitNeg"
						:limit-message="t('generator.lengthy_prompt')"
						:use-larger-font="largerFont"
						:is-blinking="blink"
						:info="t('generator.info_neg_prompt')"
						type="negative"
						mode="tweak"
					/>
				</div>

				<GeneratorPanel
					v-if="selectedInpaint !== 'style_convert'"
					id="generator-modifiers"
					:title="t('boosters.title', { count: tweakPrompt.modifiers.length })"
					:icon="FlaskConical"
				>
					<template #headerActions>
						<div
							v-if="newModifiers"
							class="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 rounded-lg shadow-lg"
						>
							<div class="w-2 h-2 bg-purple-400 rounded-full" />
							<span class="text-xs font-medium menusm:hidden 2xl:block">{{ newModifiersCount }} {{ t(`common.new${newModifiersCount > 1 ? 's' : ''}`) }}</span>
						</div>
						<Button
							class="button-purple-shade !p-1"
							@click="useDynamicModalStore().showModal(DynamicModalType.GeneratorModifiers, {
								props: {
									mode: 'tweak',
								},
							})"
						>
							<Plus class="lucide-button-icon" />
						</Button>
					</template>
					<template #content>
						<div class="flex flex-col gap-1 justify-between w-11/12">
							<span
								v-if="!tweakPrompt.modifiers.length"
								class="text-slate-300 italic text-xsp pl-1"
							>
								{{ t('boosters.boosters_info') }}
							</span>
							<div
								v-else
								class="mt-auto flex flex-col flex-nowrap gap-2"
							>
								<TransitionGroup
									name="list"
									:css="true"
								>
									<div
										v-for="(modifier, index) in tweakPrompt.modifiers"
										:key="index"
									>
										<ModifierSlot
											:modifier="modifier"
											mode="tweak"
											@update:weight="genStore.updateModifierWeight(index, $event, 'tweak')"
											@remove="genStore.removeModifier(modifier.id, 'tweak')"
										/>
									</div>
								</TransitionGroup>
							</div>
						</div>
					</template>
				</GeneratorPanel>
				<GeneratorPanel
					:title="t('common.settings')"
					:icon="Settings2"
				>
					<template #headerActions>
						<span
							class="flex-center gap-1 text-sm font-medium"
							:class="tweakSettingCost ? '' : 'text-slate-300'"
						>
							<span>
								{{ tweakSettingCost ? '+' : '' }}{{ tweakSettingCost }}
							</span>
							<component
								:is="hasDailyTickets ? EnergyIcon : Ticket"
								v-bind="{ variant: 'normal' }"
								class="lucide-button-icon-xs"
							/>
						</span>
					</template>
					<template #content>
						<MultiChoice
							v-if="selectedInpaint === 'inpaint'"
							id="generator-strength"
							v-model="tweakPrompt.inpaint_type"
							:title="t('generator.strength')"
							:icon="Layers"
							:choices="CTweakActions"
							:info="t('generator.info_tweak_strength')"
						/>
						<GeneratorSwitch
							id="generator-face-restore"
							v-model="tweakPrompt.automatic_face_restore"
							:icon="Smile"
							:title="t('generator.auto_face_restore')"
							:info="t('generator.info_auto_face_restore')"
							:cost="0"
						/>
						<GeneratorSwitch
							v-if="selectedInpaint === 'inpaint'"
							id="generator-less-nsfw"
							v-model="tweakPrompt.less_nsfw"
							:icon="Shirt"
							:title="t('generator.less_nsfw')"
							:info="t('generator.info_less_nsfw')"
							:cost="0"
						/>
						<GeneratorSwitch
							id="generator-fastlane"
							v-model="tweakPrompt.fastLane"
							:icon="PlaneTakeoff"
							:title="t('generator.fast_lane')"
							:cost="4"
							:info="t('generator.info_fast_lane')"
							guide="#fastlane"
						/>
						<GeneratorSeed
							id="generator-seed"
							v-model="tweakPrompt"
							:lockable="false"
							:icon="Bean"
							:title="t('generator.seed')"
						/>
					</template>
				</GeneratorPanel>
			</div>

			<div class="z-[1] flex-center flex-col gap-2 sticky py-6 w-full bottom-0 bg-slate-900 border-0 border-t-2 border-solid border-slate-800">
				<div
					v-if="isPremium"
					class="w-full h-[24px] relative"
				>
					<div class="bg-blue-900 w-full h-full rounded absolute" />
					<div
						class="h-full absolute bg-blue-600 rounded"
						:style="{ width: `${(user.daily_token / 1200) * 100}%` }"
					/>
					<div class="absolute px-2 font-semibold text-xsp flex items-center justify-between w-full top-1/2 -translate-y-1/2">
						<span class="flex-center gap-1">
							<Button
								v-if="hasDailyTickets"
								class="reset-btn"
								@click="toggleInfoReset"
							>
								<Info class="lucide-button-icon" />
							</Button>

							{{ t('common.energy') }}
						</span>

						<span
							v-if="hasDailyTickets"
							class="flex-center gap-1"
						>
							{{ user.daily_token }} / 1200
							<component
								:is="hasDailyTickets ? EnergyIcon : Ticket"
								v-bind="{ variant: 'normal' }"
								class="lucide-button-icon-xs"
							/>
						</span>
						<span
							v-else
							class="flex-center gap-1"
						>
							{{ t('reset_timer', { timer: nextReset }) }}
						</span>
					</div>
				</div>

				<router-link
					v-if="!isPremium"
					to="/tickets"
					class="no-underline select-none duration-300 hover:bg-slate-700 cursor-pointer hover:text-white text-xsp ml-auto text-slate-300 flex-center gap-1 border-[1px] border-slate-600 border-solid py-1 px-2 rounded-full"
				>
					<InfoIcon class="lucide-button-icon-xs text-slate-300" />
					{{ t('common.upgrade_for_more') }}
				</router-link>

				<ConfirmPopup
					group="info-reset"
				>
					<template #container>
						<div class="flex-center gap-1 text-sm p-2">
							{{ t('reset_timer', { timer: nextReset }) }}
						</div>
					</template>
				</ConfirmPopup>
				<div class="w-full flex-center gap-1 h-[56px]">
					<Button
						class="button-shadow w-full h-full !py-2 transform active:scale-x-90 transition-transform"
						:class="{
							'button-rainbow-shade': (user.daily_token < tweakCost || !isPremium),
							'button-blue-shade': isPremium && user.daily_token >= tweakCost,
						}"
						:disabled="(computedTweakImage && computedTweakImage.render === RenderType.Realistic && inpaintMode) || disableInpaint || !enoughTickets || !tweakImages.length || tweakProgress?.is_updating"
						@click="generate"
					>
						<template #default>
							<span
								v-if="timer.remaining.value <= 0"
								class="absolute top-1/2 -translate-y-1/2 right-2 flex-center gap-1 text-sm font-semibold"
							>
								{{ tweakCost }}
								<component
									:is="hasDailyTickets ? EnergyIcon : Ticket"
									v-bind="{ variant: 'normal' }"
									class="lucide-button-icon"
								/>
							</span>
							<span
								class="flex-center gap-1 w-full font-extrabold text-lg"
								:class="{ '!text-sm': timer.remaining.value > 0 }"
							>
								<template v-if="!enoughTickets">
									{{ t('common.buy_tickets') }}
								</template>
								<template v-else-if="timer.remaining.value <= 0">
									<LoaderCircle
										v-if="disableInpaint"
										class="lucide-button-icon-sm animate-spin"
									/>
									<WandSparkles
										v-else
										class="lucide-button-icon"
									/>
									<span
										v-if="tweakProgress
											&& tweakProgress.is_updating
											&& typeof tweakProgress.state === 'number'"
									>{{ t(`generator.tasks.${tweakProgress.state}`) }}</span>
									<span v-else>{{ t('common.tweak') }}</span>
								</template>
								<div
									v-else
									class="flex items-center text-start flex-nowrap w-full justify-between"
								>
									<span>{{ t('generator.many_failures') }}</span>
									<span>
										{{ timer.remaining.value.toFixed(2) }}s
									</span>
								</div>

							</span>
						</template>
					</Button>
				</div>
			</div>
		</template>
		<template #content>
			<InpaintCanvas
				v-if="tweakImages.length"
				ref="inpaint_canvas"
			/>
			<div
				v-else
				class="flex items-center gap-2 mt-[10px] menusm:mt-[125px] menu:mt-[85px] flex-col w-full"
			>
				<div class="font-extrabold text-2xl relative w-fit px-2 py-1 bg-slate-700 rounded-xl">
					{{ t('common.tweak') }}
				</div>
				<span class="text-slate-300 tracking-tight text-center">{{ t('generator.no_image_message.subtitle_tweak') }}</span>
			</div>
		</template>
	</GeneratorTemplate>
<!-- 	<Footer /> -->
</template>

<style lang="scss">

</style>

<style scoped lang="scss">
.prompt-container {
    @apply flex flex-col gap-5 w-[100%] mx-auto;
}

.header {
    @apply text-sm font-extrabold flex items-center gap-1;
}

.setting-container {
    @apply flex w-[100%] mx-auto gap-2 gen-lg:gap-4 items-start px-1;
    & > .setting {
        @apply flex flex-col gap-1 w-6/12;
    }
}

.small-info {
    @apply text-xsp text-slate-300;
}

.orange-slider :deep(.p-slider-range) {
    @apply bg-orange-500
}

.orange-slider :deep(.p-slider-handle) {
	@apply bg-orange-500;
    &:focus-within::before {
        @apply bg-orange-700;
    }
}

@keyframes blink {
  0%, 100% { @apply border-slate-500 }
  50% { @apply border-yellow-500 } /* Change la couleur selon ton besoin */
}

.animate-blink {
  animation: blink 0.2s linear 3;
}
</style>
